/**
 * Leases Page
 * Lists all leases across the manager's portfolios with state filtering.
 * Mobile-first: filter chips + card list.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrackFeature } from '@resida/analytics';
import { FileText } from 'lucide-react';
import { type LeaseStateFilter, formatUnitLabel } from '@resida/shared';
import { listAllLeases, type LeaseWithContext } from '../lib/api';
import { PageHeader, EmptyState, StatusBadge } from '../components/layout/index';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { UnitPickerModal } from '../components/lease/UnitPickerModal';
import { formatLocalDate } from '../utils/date';

type StateFilter = LeaseStateFilter;

const STATE_CHIPS: { value: StateFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'ending', label: 'Ending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'ended', label: 'Ended' },
  { value: 'archived', label: 'Archived' },
];

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}


export function LeasesPage() {
  useTrackFeature('leases', 'landlord');
  const navigate = useNavigate();
  const [leases, setLeases] = useState<LeaseWithContext[]>([]);
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUnitPickerOpen, setIsUnitPickerOpen] = useState(false);

  const fetchLeases = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await listAllLeases({ state: stateFilter });
      setLeases(response.leases);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leases');
    } finally {
      setIsLoading(false);
    }
  }, [stateFilter]);

  useEffect(() => {
    fetchLeases();
  }, [fetchLeases]);

  const handleUnitSelected = (unitId: string) => {
    setIsUnitPickerOpen(false);
    navigate(`/units/${unitId}?createLease=true`);
  };

  if (isLoading) {
    return <LeasesPageSkeleton />;
  }

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="leases-page">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Leases"
          subtitle={`${leases.length} lease${leases.length !== 1 ? 's' : ''}`}
          action={
            <Button onClick={() => setIsUnitPickerOpen(true)} data-testid="create-lease-button">
              Create Lease
            </Button>
          }
        />
      </div>

      {error && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert">
          <p className="text-destructive">{error}</p>
          <button onClick={fetchLeases} type="button" className="mt-1 inline-flex min-h-[44px] items-center text-sm font-medium text-destructive underline">Retry</button>
        </div>
      )}

      {/* Filter chips */}
      <div className="px-[var(--page-padding-x)] mb-4 flex gap-1.5 overflow-x-auto scrollbar-none">
        {STATE_CHIPS.map((chip) => (
          <button
            key={chip.value}
            type="button"
            onClick={() => setStateFilter(chip.value)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              stateFilter === chip.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
            data-testid={chip.value === stateFilter ? 'active-filter' : undefined}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* Lease list */}
      {leases.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={stateFilter === 'all' ? 'No Leases Yet' : `No ${stateFilter} leases`}
          description={
            stateFilter === 'all'
              ? 'Create your first lease to get started.'
              : 'Try changing the filter to see more leases.'
          }
          action={
            stateFilter === 'all' ? (
              <Button onClick={() => setIsUnitPickerOpen(true)} data-testid="create-lease-button-empty">
                Create Lease
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="flex flex-col">
          {leases.map((lease) => {
            const tenantNames = lease.tenants.length > 0
              ? lease.tenants.map((t) => `${t.firstName} ${t.lastName}`).join(', ')
              : null;
            const isEnded = lease.state === 'ended' || lease.state === 'archived';
            const dateLabel = isEnded
              ? `ended ${formatLocalDate(lease.actualEndDate ?? lease.endDate)}`
              : `ends ${formatLocalDate(lease.endDate)}`;

            return (
              <button
                key={lease.id}
                type="button"
                className="flex flex-col gap-1 px-[var(--page-padding-x)] py-3 min-h-[48px] border-b border-border last:border-b-0 hover:bg-accent transition-colors text-left w-full cursor-pointer"
                onClick={() => navigate(`/leases/${lease.id}`)}
                data-testid={`lease-item-${lease.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-foreground truncate">
                    {formatUnitLabel(lease.unitNumber) ? `${formatUnitLabel(lease.unitNumber)} · ${lease.propertyName}` : lease.propertyName}
                  </p>
                  <StatusBadge status={lease.state} size="sm" />
                </div>
                {tenantNames && (
                  <p className="text-xs text-muted-foreground truncate">{tenantNames}</p>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatCurrency(lease.rentAmount)}/mo · {dateLabel}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <UnitPickerModal
        isOpen={isUnitPickerOpen}
        onClose={() => setIsUnitPickerOpen(false)}
        onSelect={handleUnitSelected}
      />
    </div>
  );
}

function LeasesPageSkeleton() {
  return (
    <div className="max-w-4xl mx-auto pb-6">
      <div className="px-[var(--page-padding-x)] pt-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-20 mt-2" />
      </div>
      <div className="px-[var(--page-padding-x)] mt-4 flex gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-20 rounded-full" />
        ))}
      </div>
      <div className="px-[var(--page-padding-x)] mt-4 space-y-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    </div>
  );
}

export default LeasesPage;
